import videojs from "video.js/core.es.js";
import { must } from "@/common/utils/must";
import type Component from "video.js/dist/types/component";
import type MenuButtonClass from "video.js/dist/types/menu/menu-button";
import type MenuItemClass from "video.js/dist/types/menu/menu-item";
import type Player from "video.js/dist/types/player";
import type {
  GroupedSources,
  ResolutionMenuItemOptions,
  ResolutionPlayer,
  ResolutionPlugin,
  ResolutionSettings,
  ResolutionSource,
  ResolutionState,
  SourcePicker,
} from "@/common/videojs/resolutionTypes";

(function () {
  const defaults: ResolutionSettings = {
    ui: true,
  };

  const MenuItem = videojs.getComponent("MenuItem") as typeof MenuItemClass;

  class ResolutionMenuItem extends MenuItem {
    declare src: ResolutionSource[];
    declare _onResolutionChange: () => void;

    constructor(player: Player, options: ResolutionMenuItemOptions) {
      options.selectable = true;
      super(player, options);
      this.src = options.src;
      this._onResolutionChange = this.update.bind(this);
      player.on("resolutionchange", this._onResolutionChange);
    }

    dispose() {
      if (this.player_) {
        this.player_.off("resolutionchange", this._onResolutionChange);
      }
      super.dispose();
    }

    handleClick(event: Event) {
      super.handleClick(event);
      this.player_.currentResolution(this.options_.label);
    }

    update() {
      const selection = this.player_.currentResolution();
      this.selected(this.options_.label === selection.label);
    }
  }
  videojs.registerComponent(
    "ResolutionMenuItem",
    ResolutionMenuItem as typeof Component,
  );

  const MenuButton = videojs.getComponent(
    "MenuButton",
  ) as typeof MenuButtonClass;

  class ResolutionMenuButton extends MenuButton {
    declare label: HTMLSpanElement;
    declare _onUpdateSources: () => void;
    declare sources: GroupedSources | undefined;
    declare currentSelection: ResolutionState | undefined;

    constructor(player: Player, options?: ResolutionSettings) {
      const opts = Object.assign({}, options || {}, { label: "Quality" });
      super(player, opts);
      this.label = document.createElement("span");
      this.el().setAttribute("aria-label", "Quality");
      this.controlText("Quality");

      if (opts.dynamicLabel) {
        videojs.dom.addClass(this.label, "vjs-resolution-button-label");
        this.el().appendChild(this.label);
      } else {
        const staticLabel = document.createElement("span");
        videojs.dom.addClass(staticLabel, "vjs-menu-icon");
        this.el().appendChild(staticLabel);
      }
      this._onUpdateSources = this.update.bind(this);
      player.on("updateSources", this._onUpdateSources);
    }

    dispose() {
      if (this.player_) {
        this.player_.off("updateSources", this._onUpdateSources);
      }
      super.dispose();
    }

    createItems() {
      const menuItems: ResolutionMenuItem[] = [];
      const labels = (this.sources && this.sources.label) || {};

      for (const key in labels) {
        if (Object.hasOwn(labels, key)) {
          menuItems.push(
            new ResolutionMenuItem(this.player_, {
              label: key,
              src: (labels as Record<string, ResolutionSource[]>)[key],
              selected:
                key ===
                (this.currentSelection ? this.currentSelection.label : false),
            }),
          );
        }
      }
      return menuItems;
    }

    update() {
      this.sources = this.player_.getGroupedSrc();
      this.currentSelection = this.player_.currentResolution();
      if (this.label) {
        this.label.innerHTML = this.currentSelection
          ? this.currentSelection.label
          : "";
      }
      return super.update() as undefined;
    }

    buildCSSClass() {
      return `${super.buildCSSClass()} vjs-resolution-button`;
    }
  }
  videojs.registerComponent(
    "ResolutionMenuButton",
    ResolutionMenuButton as typeof Component,
  );

  /**
   * Initialize the plugin.
   */
  const videoJsResolutionSwitcher: ResolutionPlugin = function (
    this: Player,
    options,
  ) {
    // video.js 8.x: videojs.obj.merge (obj.mergeOptions not provided on videojs.obj in 8.x).
    const settings: ResolutionSettings = videojs.obj.merge(defaults, options),
      vjsPlayer = this,
      player: ResolutionPlayer = this;

    /**
     * Updates player sources or returns current source URL
     */
    player.updateSrc = function (this: ResolutionPlayer, src) {
      //Return current src if src is not given
      if (!src) {
        return player.src();
      }

      // Only add those sources which we can (maybe) play
      src = src.filter(function (source) {
        try {
          return player.canPlayType(source.type) !== "";
        } catch {
          // If a Tech doesn't yet have canPlayType just add it
          return true;
        }
      });
      //Sort sources
      const sorted = src.sort(compareResolutions);
      this.currentSources = sorted;
      this.groupedSrc = bucketSources(sorted);
      // Pick one by default
      const chosen = chooseSrc(this.groupedSrc, sorted);
      this.currentResolutionState = {
        label: chosen.label,
        sources: chosen.sources,
      };

      player.trigger("updateSources");
      player.setSourcesSanitized(chosen.sources, chosen.label);
      player.trigger("resolutionchange");
      return player;
    };

    /**
     * Returns current resolution or sets one when label is specified
     */
    function currentResolution(
      this: ResolutionPlayer,
      label?: null,
    ): ResolutionState;
    function currentResolution(
      this: ResolutionPlayer,
      label: string,
      customSourcePicker?: SourcePicker,
    ): ResolutionPlayer | undefined;
    function currentResolution(
      this: ResolutionPlayer,
      label?: string | null,
      customSourcePicker?: SourcePicker,
    ) {
      if (label == null) {
        return this.currentResolutionState;
      }

      // Lookup sources for label
      if (
        !this.groupedSrc ||
        !this.groupedSrc.label ||
        !this.groupedSrc.label[label]
      ) {
        return;
      }
      const sources = this.groupedSrc.label[label];
      // Remember player state
      const currentTime = player.currentTime();
      const isPaused = player.paused();

      // Hide bigPlayButton
      if (!isPaused && this.player_.options_.bigPlayButton) {
        this.player_.bigPlayButton.hide();
      }

      // Change player source and wait for loadeddata event, then play video
      // loadedmetadata doesn't work right now for flash.
      // Probably because of https://github.com/videojs/video-js-swf/issues/124
      // If player preload is 'none' and then loadeddata not fired. So, we need timeupdate event for seek handle (timeupdate doesn't work properly with flash)
      let handleSeekEvent = "loadeddata";
      if (
        this.player_.techName_ !== "Youtube" &&
        this.player_.preload() === "none" &&
        this.player_.techName_ !== "Flash"
      ) {
        handleSeekEvent = "timeupdate";
      }
      player
        .setSourcesSanitized(
          sources,
          label,
          customSourcePicker || settings.customSourcePicker,
        )
        .one(handleSeekEvent, function () {
          player.currentTime(currentTime);
          if (!isPaused) {
            // Start playing and hide loadingSpinner (flash issue ?)
            player.play();
            vjsPlayer["handleTechSeeked_"]();
          }
          player.trigger("resolutionchange");
        });
      return player;
    }
    player.currentResolution = currentResolution;

    player.defaultResolution = function (res) {
      if (res == null) {
        return settings["default"];
      }
      settings["default"] = res;
    };

    /**
     * Returns grouped sources by label, resolution and type
     */
    player.getGroupedSrc = function (this: ResolutionPlayer) {
      return this.groupedSrc;
    };

    player.setSourcesSanitized = function (
      this: ResolutionPlayer,
      sources,
      label,
      customSourcePicker,
    ) {
      this.currentResolutionState = {
        label: label,
        sources: sources,
      };
      if (typeof customSourcePicker === "function") {
        return customSourcePicker(player, sources, label);
      }
      player.src(
        sources.map(function (src) {
          return { src: src.src, type: src.type, res: src.res };
        }),
      );
      return player;
    };

    /**
     * Method used for sorting list of sources
     */
    function compareResolutions(a: ResolutionSource, b: ResolutionSource) {
      if (!a.res || !b.res) {
        return 0;
      }
      return +b.res - +a.res;
    }

    /**
     * Group sources by label, resolution and type
     */
    function bucketSources(src: ResolutionSource[]): GroupedSources {
      const resolutions: GroupedSources = {
        label: {},
        res: {},
        type: {},
      };
      src.map(function (source) {
        initResolutionKey(resolutions, "label", source);
        initResolutionKey(resolutions, "res", source);
        initResolutionKey(resolutions, "type", source);

        appendSourceToKey(resolutions, "label", source);
        appendSourceToKey(resolutions, "res", source);
        appendSourceToKey(resolutions, "type", source);
      });
      return resolutions;
    }

    function initResolutionKey(
      resolutions: GroupedSources,
      key: keyof GroupedSources,
      source: ResolutionSource,
    ) {
      if (resolutions[key][source[key]] == null) {
        resolutions[key][source[key]] = [];
      }
    }

    function appendSourceToKey(
      resolutions: GroupedSources,
      key: keyof GroupedSources,
      source: ResolutionSource,
    ) {
      resolutions[key][source[key]].push(source);
    }

    /**
     * Choose src if option.default is specified
     */
    function chooseSrc(groupedSrc: GroupedSources, src: ResolutionSource[]) {
      let selectedRes = settings["default"]; // use array access as default is a reserved keyword
      let selectedLabel = "";
      if (selectedRes === "high") {
        selectedRes = src[0].res;
        selectedLabel = src[0].label;
      } else if (
        selectedRes === "low" ||
        selectedRes == null ||
        !groupedSrc.res[selectedRes]
      ) {
        // Select low-res if default is low or not set
        selectedRes = src[src.length - 1].res;
        selectedLabel = src[src.length - 1].label;
      } else if (groupedSrc.res[selectedRes]) {
        selectedLabel = groupedSrc.res[selectedRes][0].label;
      }

      return {
        res: selectedRes,
        label: selectedLabel,
        sources: groupedSrc.res[selectedRes],
      };
    }

    player.ready(function () {
      if (settings.ui) {
        const menuButton = new ResolutionMenuButton(vjsPlayer, settings);
        // Anchor the resolution menu just before the fullscreen toggle. The
        // toggle is absent when fullscreen is unavailable (e.g. a denied
        // Permissions-Policy in an embed) — a null anchor makes insertBefore
        // append at the end, which keeps the button on the control bar.
        const fullscreenToggle = player.controlBar.getChild("fullscreenToggle");
        const switcher: Node & { dispose?: () => void } =
          player.controlBar.el_.insertBefore(
            menuButton.el_,
            fullscreenToggle ? fullscreenToggle.el_ : null,
          );
        player.controlBar.resolutionSwitcher = switcher;
        switcher.dispose = function (this: Node) {
          must(this.parentNode, "resolution switcher parent").removeChild(this);
        };
      }
      const tagSources = must(player.options_.sources, "player sources");
      if (tagSources.length > 1) {
        // tech: Html5 and Flash
        // Create resolution switcher for videos form <source> tag inside <video>
        player.updateSrc(tagSources);
      }
    });
  };

  // register the plugin
  videojs.registerPlugin(
    "videoJsResolutionSwitcher",
    videoJsResolutionSwitcher,
  );
  // @ts-expect-error the IIFE takes no parameters; the call passes two anyway.
})(window, videojs);

export default "ResolutionSwitcher";
